import { useState } from 'react';

import type { AssistedRunReport } from '@nightshift/ui-types';

import { BudgetsIcon, OpsIcon, RunsIcon } from '../shell/icons.tsx';
import type { ViewName } from '../lib/router.ts';
import { assistedRunReportUrl, defaultReportWindow } from '../lib/report-window.ts';
import { api } from '../live/api.ts';
import { compactDuration, usd } from '../lib/format.ts';

/**
 * Reports landing — Ops, Runs and Budgets grouped under one rail entry.
 *
 * Those three views keep their own URLs. This page is the rail's Reports destination so an
 * operator who does not already know `/ops` still finds them.
 */
export function ReportsView({
  onGo,
  showBudgets,
  projectSlug = null,
}: {
  onGo: (view: ViewName) => void;
  showBudgets: boolean;
  projectSlug?: string | null;
}) {
  const initial = defaultReportWindow(new Date());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [tz, setTz] = useState(initial.tz);
  const url = (format: 'md' | 'json' | 'csv') => assistedRunReportUrl({ from, to, tz, project: projectSlug, format });
  const [report, setReport] = useState<AssistedRunReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="reports-landing">
      <section className="reports-run" aria-label="Assisted run report">
        <h2 className="reports-run-title">Assisted run report</h2>
        <p className="reports-landing-lead">
          An explicit window and timezone: every touched ticket's states, merges, interventions, repeated
          failures and remaining work, from the recorded events. Markdown to read; JSON or CSV to add up.
        </p>
        <form className="reports-run-form" onSubmit={(event) => event.preventDefault()}>
          <label>
            <span>From</span>
            <input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label>
            <span>To</span>
            <input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
          <label>
            <span>Timezone</span>
            <input value={tz} onChange={(event) => setTz(event.target.value)} />
          </label>
          <div className="reports-run-links">
            <button
              type="button"
              className="btn btn-accent btn-sm"
              disabled={loading}
              onClick={() => {
                setLoading(true);
                setError(null);
                api
                  .assistedRunReport(url('json'))
                  .then(setReport)
                  .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : String(caught)))
                  .finally(() => setLoading(false));
              }}
            >
              {loading ? 'Building…' : 'Show here'}
            </button>
            <a className="btn btn-sm" href={url('md')} target="_blank" rel="noreferrer">Markdown</a>
            <a className="btn btn-sm" href={url('json')} target="_blank" rel="noreferrer">JSON</a>
            <a className="btn btn-sm" href={url('csv')} target="_blank" rel="noreferrer">CSV</a>
          </div>
        </form>
        {error ? <p className="bad">{error}</p> : null}
        {report ? <AssistedRunReportView report={report} /> : null}
      </section>
      <header className="reports-landing-head">
        <h1 className="reports-landing-title">Reports</h1>
        <p className="reports-landing-lead">
          Ops, Runs and Budgets stay at their own addresses. This is the grouped way in.
        </p>
      </header>
      <div className="reports-cards">
        <button type="button" className="reports-card" onClick={() => onGo('ops')}>
          <OpsIcon size={18} />
          <span className="reports-card-name">Ops</span>
          <span className="reports-card-hint">Machine health, queue and spend</span>
        </button>
        <button type="button" className="reports-card" onClick={() => onGo('runs')}>
          <RunsIcon size={18} />
          <span className="reports-card-name">Runs</span>
          <span className="reports-card-hint">Every attempt and sidecar job, with transcripts</span>
        </button>
        {showBudgets ? (
          <button type="button" className="reports-card" onClick={() => onGo('budgets')}>
            <BudgetsIcon size={18} />
            <span className="reports-card-name">Budgets</span>
            <span className="reports-card-hint">Performance targets over time</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The report on the page: the same records the Markdown export renders, as tables a person can
 * scan without downloading anything. Every number is what the records say; unknown cost is
 * counted as unknown.
 */
function AssistedRunReportView({ report }: { report: AssistedRunReport }) {
  const interventions = Object.entries(report.interventionsByAction).sort((a, b) => b[1] - a[1]);
  const repairs = Object.entries(report.repairsByCategory).sort((a, b) => b[1] - a[1]);
  return (
    <div className="reports-run-body" data-testid="assisted-run-report">
      <p className="dim">
        {report.tickets.length} {report.tickets.length === 1 ? 'ticket' : 'tickets'} touched · {report.merged.length} merged · {report.interventions.length}{' '}
        interventions · known cost {usd(report.cost.knownUsd)}
        {report.cost.unknownInvocations > 0 ? ` (+${report.cost.unknownInvocations} invocations with unknown cost)` : ''}
      </p>
      <table className="reports-run-table">
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Start</th>
            <th>End</th>
            <th className="num">Wall</th>
            <th className="num">Resource</th>
            <th className="num">Cost</th>
            <th>Holding</th>
          </tr>
        </thead>
        <tbody>
          {report.tickets.map((row) => (
            <tr key={row.ticketKey} data-merged={row.mergedInWindow ? 'yes' : 'no'}>
              <td>{row.ticketKey}</td>
              <td>{row.stateAtStart ?? 'new'}</td>
              <td>{row.stateAtEnd}</td>
              <td className="num">{compactDuration(row.wallMs)}</td>
              <td className="num">{compactDuration(row.resourceMs)}</td>
              <td className="num">{row.costUsd === null ? 'unknown' : usd(row.costUsd)}</td>
              <td>{row.holding}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="reports-run-columns">
        <div>
          <h3 className="reports-run-subtitle">Interventions</h3>
          {interventions.length === 0 ? <p className="dim">None.</p> : (
            <ul>
              {interventions.map(([action, count]) => (
                <li key={action}>{action} ×{count}</li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="reports-run-subtitle">Repairs</h3>
          {repairs.length === 0 ? <p className="dim">None.</p> : (
            <ul>
              {repairs.map(([category, count]) => (
                <li key={category}>{category} ×{count}</li>
              ))}
            </ul>
          )}
          {report.recurrence.length > 0 ? (
            <p className="dim">Repeated: {report.recurrence.map((row) => `${row.ticketKey} ${row.category} ×${row.count}`).join(', ')}</p>
          ) : null}
        </div>
        <div>
          <h3 className="reports-run-subtitle">Remaining</h3>
          {report.remaining.length === 0 ? <p className="dim">Nothing left open.</p> : (
            <ul>
              {report.remaining.map((row) => (
                <li key={row.ticketKey}>
                  {row.ticketKey} · {row.state} · <span className="dim">{row.holding}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <p className="dim reports-run-provenance">{report.provenance}</p>
    </div>
  );
}
